import type { CSSProperties, ReactNode } from 'react'
import { useLocale } from '@/i18n/locale'
import { track } from '@/lib/analytics'
import { NavLink } from '@/components/NavLink'
import { ROLES } from '@/data/roles'
import { BUSINESS_FUNCTIONS } from '@/sections/Hero/functions'
import { ContextFlowMock } from './mocks/ContextFlowMock'
import { OwnerWindowMock } from './mocks/OwnerWindowMock'
import './how-it-works.css'

/**
 * Блок «Как это устроено» (ТЗ 4.6; срез 2026-09-21 по конверсионному аудиту).
 *
 * Было три шага с макетами на пять экранов. Собственник-рецензент понял
 * продукт только на макете «окно собственника», а схему «слоя
 * систематизации» читал как чертёж для инженера. Поэтому окно — герой
 * блока и стоит сразу под вводкой; схема ушла под раскрытие (в разметке
 * остаётся — для поиска), а шаг «диагностика» убран: он подробно стоит у
 * формы в #final-cta.
 *
 * Якоря: #coverage держит порядок секций, #methodology-stages — пункт меню
 * «Методология» и адрес /method. Фраза про обязанность Хранителя стоит
 * дословно — на неё смотрит страж обещаний; «Everything in one window»,
 * «Measured before the stage is paid» и «Seven steps» — маркеры стража /en.
 * 21 функция — видимым текстом (страж hero-functions читает innerText, и
 * закрытый <details> для него пуст), роли — ссылками (страж home-links).
 */
const COPY = {
  ru: {
    eyebrow: 'Как это устроено · что входит',
    heading: 'Весь бизнес — в одном окне',
    lede: 'Многим кажется, что внедрение ИИ в бизнес под ключ — это один умный агент. На деле их понадобятся десятки, и заказывать каждого отдельно — долго и дорого. Мы ставим фабрику агентов: компания описана один раз, навыки собраны в библиотеку — новых агентов из них собирает уже ваша команда.',
    kicker: 'Замер — до оплаты этапа',
    lead: 'Больше не нужно покупать ботов, агентов и интеграторов',
    text: 'У каждого агента свой счётчик: задачи, часы, ошибки. Приёмка идёт по письменному критерию — до оплаты этапа. Что делать с высвобожденным ресурсом — снимать ФОТ или брать больше задач тем же составом — решает собственник. Подряды и аутсорс закрываются внутри компании.',
    calcLink: 'Посчитать на своих цифрах',
    plain: [
      'Данные из учётной системы, CRM, телефонии и банков приходят сами, по расписанию.',
      'Навык пишется один раз и работает во всех агентах.',
      'Система остаётся вашей при любом исходе: ежемесячная выгрузка контекста в ваше хранилище — отдельная обязанность Хранителя.',
    ],
    details: 'Как это устроено внутри',
    detailsNote: 'Схема слоя систематизации: откуда приходят данные, кто их разбирает и в каких трёх хранилищах они лежат.',
    guideLink: 'Архитектура контекста — подробный разбор',
    rolesLabel: 'Готовые роли',
    roles: {
      yurist: 'ИИ-юрист для бизнеса',
      assistent: 'ИИ-ассистент собственника',
      analitik: 'ИИ-аналитик',
      buhgalter: 'ИИ-бухгалтер',
      findir: 'ИИ-финдиректор',
      marketolog: 'ИИ-маркетолог',
      prodavec: 'ИИ-продавец',
    } as Record<string, string>,
    services: [
      ['/services/ai-crm', 'ИИ-CRM'],
      ['/services/rag-baza-znaniy', 'RAG-база знаний'],
    ],
    functionsLabel: '21 функция бизнеса в одной системе',
    timeEyebrow: 'Методология',
    timeHeading: '2–3 месяца до приёмки',
    timeLede: 'Первый результат — на пилоте, через 4–6 недель. Вся система готова к приёмке за 2–3 месяца.',
    stages: [
      ['30–90 мин · бесплатно', 'ИИ-диагностика'],
      ['~1 неделя', 'Аккаунты и доступы для команды'],
      ['~1–3 недели', 'Подключаем ваши данные'],
      ['~2 недели', 'Пилот на одной задаче'],
      ['~3–5 недель', 'Автоматизации по ролям'],
      ['дальше', 'Хранитель и сопровождение'],
    ],
    bookLink: 'Записаться',
    methodLink: 'Семь шагов, 46 задач с критерием приёмки — методология целиком',
  },
  en: {
    eyebrow: 'How it works · what’s included',
    heading: 'Everything in one window',
    lede: 'Many assume turnkey AI implementation for a business means one smart agent. In practice a company needs dozens, and ordering each one separately is slow and costly. We set up an agent factory: the company is described once, skills are kept in a library — and from these your own team assembles new agents.',
    kicker: 'Measured before the stage is paid',
    lead: 'No more buying bots, agents and integrators',
    text: 'Every agent carries its own counter: tasks, hours, errors. Acceptance runs against a written criterion — before the stage is paid. What to do with the freed-up capacity — cut payroll or take on more work with the same team — is the owner’s call. Subcontracts and outsourcing get closed inside the company.',
    calcLink: 'Run it on your numbers',
    plain: [
      'Data from the accounting system, CRM, telephony and banks arrives by itself, on schedule.',
      'A skill is written once and works in every agent.',
      'The system stays yours whatever happens: a monthly context export into your own storage is a separate duty of the Keeper.',
    ],
    details: 'How it is built inside',
    detailsNote: 'The systematization layer: where the data comes from, who sorts it and which three stores it lives in.',
    guideLink: '',
    rolesLabel: 'Ready-made roles',
    roles: {
      yurist: 'AI lawyer for business',
      assistent: 'AI owner’s assistant',
      analitik: 'AI analyst',
      buhgalter: 'AI accountant',
      findir: 'AI CFO',
      marketolog: 'AI marketer',
      prodavec: 'AI sales rep',
    } as Record<string, string>,
    services: [
      ['/services/ai-crm', 'AI CRM'],
      ['/services/rag-baza-znaniy', 'RAG knowledge base'],
    ],
    functionsLabel: '21 business functions in one system',
    timeEyebrow: 'Methodology',
    timeHeading: '2–3 months to acceptance',
    timeLede: 'The first result comes with the pilot, in 4–6 weeks. The whole system is ready for acceptance in 2–3 months.',
    stages: [
      ['30–90 min · free', 'AI diagnostics'],
      ['~1 week', 'Accounts and access for the team'],
      ['~1–3 weeks', 'We connect your data'],
      ['~2 weeks', 'A pilot on one task'],
      ['~3–5 weeks', 'Automations per role'],
      ['onward', 'The Keeper and ongoing support'],
    ],
    bookLink: 'Book a slot',
    methodLink: 'Seven steps, 46 acceptance-tested tasks — full methodology',
  },
}

// Порядок ролей задан ТЗ (первым — юрист: кластер даёт больше половины
// показов сайта); сами роли сверяются с единым источником src/data/roles.ts.
const ROLE_ORDER = ['yurist', 'assistent', 'analitik', 'buhgalter', 'findir', 'marketolog', 'prodavec']

// Насыщенность полосы шага: от бледной диагностики к плотному сопровождению —
// тот же ход, что был у лесенки, только без шкалы недель.
const STAGE_TONE = [0.35, 0.45, 0.6, 0.7, 0.85, 1]

const LINK =
  'text-body font-medium text-ink underline decoration-divider underline-offset-4 hover:decoration-accent transition-colors'

export function HowItWorks() {
  const locale = useLocale()
  const t = COPY[locale]
  const isEn = locale === 'en'

  const roles = ROLE_ORDER.filter((slug) => ROLES.some((r) => r.slug === slug)).map(
    (slug) => [`/digital/${slug}`, t.roles[slug]] as const,
  )
  const roleLinks = [...roles, ...t.services.map(([href, label]) => [href, label] as const)]

  return (
    <section id="coverage" className="section">
      <div className="container-x relative z-10">
        <div className="max-w-4xl">
          <p className="font-mono text-small uppercase tracking-wider text-muted">{t.eyebrow}</p>
          <h2 className="mt-5 text-h2 text-ink">{t.heading}</h2>
          <p className="mt-5 max-w-3xl text-body text-muted">{t.lede}</p>
        </div>

        {/* Герой блока: окно собственника и что оно значит для денег. */}
        <div className="mt-6 grid grid-cols-1 gap-x-12 gap-y-6 sm:mt-8 lg:grid-cols-12 lg:items-center">
          <div className="min-w-0 lg:col-span-7">
            <OwnerWindowMock />
          </div>
          <div className="max-w-3xl lg:col-span-5">
            <p className="font-mono text-small uppercase tracking-wider text-muted">{t.kicker}</p>
            <h3 className="mt-3 text-h3 text-ink">{t.lead}</h3>
            <p className="mt-4 text-body text-muted">{t.text}</p>
            <div className="mt-5">
              <NavLink
                href="#calculator"
                className={LINK}
                onClick={() => track('capability_link_click', { target: 'step3_calculator' })}
              >
                {t.calcLink}{' '}<span aria-hidden>→</span>
              </NavLink>
            </div>
          </div>
        </div>

        {/* Три строки вместо схемы: что происходит с данными, навыками и владением. */}
        <ol className="mt-6 grid grid-cols-1 gap-x-10 gap-y-3 border-t border-divider pt-5 sm:mt-8 md:grid-cols-3">
          {t.plain.map((line, i) => (
            <li key={line} className="flex gap-4 md:block">
              <span className="font-mono text-small leading-relaxed text-accent md:block" aria-hidden>
                0{i + 1}
              </span>
              <p className="text-body text-ink md:mt-1">{line}</p>
            </li>
          ))}
        </ol>

        {/* Схема слоя систематизации — под раскрытием: инженеру нужна, собственнику
            мешает. Нативный <details>: содержимое остаётся в SSR-разметке. */}
        <details className="hiw-details mt-5 border-t border-divider">
          <summary className="-mx-2 flex min-h-[44px] cursor-pointer items-center justify-between gap-6 rounded-btn px-2 py-2.5 text-body font-medium text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            <span>{t.details}</span>
            <span className="hiw-details__sign font-mono text-h3 leading-none text-muted" aria-hidden />
          </summary>
          <div className="pb-4 pt-1">
            <p className="mb-5 max-w-3xl text-small text-muted">{t.detailsNote}</p>
            <ContextFlowMock />
            {/* Гайд про архитектуру контекста — русский: на /en ссылки нет. */}
            {!isEn && (
              <p className="mt-5">
                <NavLink
                  href="/guides/korporativnyy-ai-arhitektura-konteksta"
                  className={LINK}
                  onClick={() => track('capability_link_click', { target: 'step2_guide' })}
                >
                  {t.guideLink}{' '}<span aria-hidden>→</span>
                </NavLink>
              </p>
            )}
          </div>
        </details>

        {/* Роли и функции — компактно: ссылки держат кликовую дистанцию до
            страниц ролей (страж home-links), список функций — видимый текст. */}
        <div className="mt-5 space-y-2 border-t border-divider pt-5">
          <p className="text-[0.9375rem] leading-relaxed text-ink sm:text-body">
            <span className="mr-3 font-mono text-small uppercase tracking-wider text-muted">{t.rolesLabel}</span>
            {roleLinks.map(([href, label], i) => (
              <Sep key={href} first={i === 0}>
                {isEn ? (
                  <span className="whitespace-nowrap">{label}</span>
                ) : (
                  <NavLink
                    href={href}
                    className="whitespace-nowrap text-ink underline decoration-accent/50 decoration-1 underline-offset-[6px] hover:decoration-accent transition-colors"
                    onClick={() => track('capability_link_click', { target: href })}
                  >
                    {label}
                  </NavLink>
                )}
              </Sep>
            ))}
          </p>
          <p className="text-small leading-relaxed text-muted">
            <span className="mr-3 font-mono uppercase tracking-wider">{t.functionsLabel}</span>
            {BUSINESS_FUNCTIONS[locale].join(' · ')}
          </p>
        </div>

        {/* Сроки — одна полоса. Якорь #methodology-stages — цель /method и меню. */}
        <div id="methodology-stages" className="scroll-mt-20 mt-8 border-t border-divider pt-5 sm:mt-10">
          <div className="flex flex-col gap-x-12 gap-y-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="font-mono text-small uppercase tracking-wider text-muted">{t.timeEyebrow}</p>
              <h3 className="mt-3 text-h3 text-ink lg:text-h2">{t.timeHeading}</h3>
            </div>
            <p className="max-w-md text-body text-muted">{t.timeLede}</p>
          </div>

          <ol className="mt-5 grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-6 lg:gap-x-4">
            {t.stages.map(([time, title], i) => (
              <li key={title} className="hiw-stage" style={{ ['--o' as string]: STAGE_TONE[i] } as CSSProperties}>
                <div className="font-mono text-small text-accent">{time}</div>
                <div className="text-[0.9375rem] font-medium leading-snug text-ink lg:text-body">
                  {title}
                  {/* Первый шаг бесплатный — единственный призыв блока стоит прямо на нём. */}
                  {i === 0 && (
                    <>
                      {' '}
                      <NavLink
                        href="#final-cta"
                        className="whitespace-nowrap underline decoration-divider underline-offset-4 hover:decoration-accent transition-colors"
                        onClick={() => track('capability_link_click', { target: 'step1_diagnostics' })}
                      >
                        {t.bookLink}{' '}<span aria-hidden>→</span>
                      </NavLink>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>

          <p className="mt-5 text-body font-medium text-ink">
            {isEn ? (
              t.methodLink
            ) : (
              <NavLink
                href="/vnedrenie-ii-agentov#metodologiya"
                className={LINK}
                onClick={() => track('capability_link_click', { target: 'methodology' })}
              >
                {t.methodLink}{' '}<span aria-hidden>→</span>
              </NavLink>
            )}
          </p>
        </div>
      </div>
    </section>
  )
}

function Sep({ first, children }: { first: boolean; children: ReactNode }) {
  return (
    <>
      {!first && <span className="text-muted" aria-hidden> · </span>}
      {children}
    </>
  )
}
